import { useCallback, useState } from 'react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

export type EligibleInvoiceTimesheet = {
  id: number;
  order_id: number;
  job_id: number;
  worker_id: number | null;
  worker_name: string | null;
  approved_at: string | null;
  approved_checkin_at: string | null;
  approved_checkout_at: string | null;
  first_shift_starts_at: string | null;
  skill_name: string | null;
  address: string | null;
};

type EligibleInvoiceTimesheetsResponse = {
  invoice: {
    id: number;
    status: string;
    account_id: number;
    customer_id: number;
    stripe_invoice_id: string | null;
  };
  timesheets: EligibleInvoiceTimesheet[];
};

export function useEligibleInvoiceTimesheets() {
  const [data, setData] = useState<EligibleInvoiceTimesheetsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (invoiceId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosRubyClient.get(
        `/api/invoice_health/invoices/${invoiceId}/eligible_timesheets`,
      );
      setData(response.data.data);
      return { success: true };
    } catch (err: any) {
      const message =
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load eligible timesheets';
      setError(message);
      setData(null);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, loading, error, fetch, clear };
}

export function useAddDraftInvoiceTimesheets() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useCallback(
    async (params: { invoiceId: string; timesheetIds: number[] }) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axiosRubyClient.post(
          `/api/invoice_health/invoices/${params.invoiceId}/add_timesheets`,
          {
            timesheet_ids: params.timesheetIds,
          },
        );

        return { success: true, data: response.data.data };
      } catch (err: any) {
        const message =
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add timesheets';
        setError(message);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { loading, error, add, clearError };
}
